import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import Card from './Card';
import CeremonyCard from './CeremonyCard';
import { getCardRarityClass } from '../utils/cardUtils';
import { getCardImageSrc, handleCardImageError } from '../hooks/useCardImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRadiation } from '@fortawesome/free-solid-svg-icons';

const REVEAL_INTERVAL_MS = 500;

const CEREMONY_TIERS = {
    gold: { setup: 500, effects: 1000, flip: 900, settle: 500 },
    titanium: { setup: 500, effects: 1500, flip: 1300, settle: 500 },
    uranium: { setup: 500, effects: 2500, flip: 1900, settle: 500 },
};

const CEREMONY_TIER_THRESHOLDS = [
    { min: 96, tier: 'uranium' },
    { min: 86, tier: 'titanium' },
    { min: 76, tier: 'gold' },
];

const getCeremonyTier = rating =>
    CEREMONY_TIER_THRESHOLDS.find(t => rating >= t.min)?.tier ?? null;

const safeDecode = str => {
    try {
        return decodeURIComponent(str);
    } catch {
        return str;
    }
};

const PackOpenModal = ({ cards, seriesName, seriesKey, onClose, remainingCount, onOpenAnother, loading }) => {
    const sortedCards = useMemo(() => [...cards].sort((a, b) => a.rating - b.rating), [cards]);
    const [visibleCount, setVisibleCount] = useState(0);
    const [ceremonyPhase, setCeremonyPhase] = useState(null);
    const [ceremonyTier, setCeremonyTier] = useState(null);
    const [ceremonyCardIndex, setCeremonyCardIndex] = useState(null);
    const [flipInCardIndex, setFlipInCardIndex] = useState(null);
    const [flipInDuration, setFlipInDuration] = useState(null);
    const ceremonyTimers = useRef([]);

    const showAll = useCallback(() => {
        ceremonyTimers.current.forEach(clearTimeout);
        ceremonyTimers.current = [];
        setCeremonyPhase(null);
        setCeremonyTier(null);
        setCeremonyCardIndex(null);
        setFlipInCardIndex(null);
        setFlipInDuration(null);
        setVisibleCount(sortedCards.length);
    }, [sortedCards.length]);

    useEffect(() => {
        if (visibleCount >= sortedCards.length) return;
        if (ceremonyPhase) return;

        const nextIndex = visibleCount;
        const nextCard = sortedCards[nextIndex];
        const tier = getCeremonyTier(nextCard.rating);

        if (tier) {
            const kickoff = setTimeout(() => {
                setCeremonyTier(tier);
                setCeremonyCardIndex(nextIndex);
                const timing = CEREMONY_TIERS[tier];

                setCeremonyPhase('setup');
                ceremonyTimers.current = [
                    setTimeout(() => setCeremonyPhase('effects'), timing.setup),
                    setTimeout(() => {
                        setCeremonyPhase('flip');
                        setVisibleCount(prev => prev + 1);
                    }, timing.setup + timing.effects),
                    // Settle at 40% of flip — real card starts flipping in as ceremony card goes edge-on
                    setTimeout(() => {
                        setCeremonyPhase('settle');
                        setFlipInCardIndex(nextIndex);
                        setFlipInDuration(timing.flip / 1000 + 's');
                    }, timing.setup + timing.effects + Math.round(timing.flip * 0.4)),
                    setTimeout(() => {
                        setCeremonyPhase(null);
                        setCeremonyCardIndex(null);
                        const followingIndex = nextIndex + 1;
                        if (followingIndex < sortedCards.length && !getCeremonyTier(sortedCards[followingIndex].rating)) {
                            setCeremonyTier(null);
                        }
                    }, timing.setup + timing.effects + timing.flip + timing.settle),
                    // flipInCardIndex intentionally not cleared — removing --flip-in class causes blink
                    // as rarity glow animation snaps on. Class is harmless after animation ends.
                ];
            }, REVEAL_INTERVAL_MS);

            return () => clearTimeout(kickoff);
        }

        const timer = setTimeout(() => {
            setVisibleCount(prev => prev + 1);
        }, REVEAL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [visibleCount, sortedCards, ceremonyPhase]);

    const allRevealed = visibleCount >= sortedCards.length && !ceremonyPhase;
    const packImageSrc = getCardImageSrc(seriesKey);
    const isCeremonyActive = ceremonyPhase && ceremonyPhase !== 'settle';

    return (
        <Modal onClose={onClose} title={`${seriesName} Pack`}>
            <div className={`pack-open-modal${ceremonyTier ? ` pack-open-modal--ceremony-${ceremonyTier}` : ''}${ceremonyPhase ? ` pack-open-modal--ceremony-${ceremonyPhase}` : ''}`}>
                {ceremonyTier === 'uranium' && isCeremonyActive ? (
                    <div className="ceremony-uranium-icons">
                        {Array.from({ length: 6 }, (_, i) => (
                            <span
                                key={i}
                                className="ceremony-uranium-icon"
                                style={{
                                    left: `${(i / 6) * 100 + 5}%`,
                                    fontSize: `${30 + (i % 3) * 20}px`,
                                    animationDuration: `${3 + (i % 3)}s`,
                                    animationDelay: `${(i * 0.5) % 2}s`,
                                }}
                            >
                                <FontAwesomeIcon icon={faRadiation} />
                            </span>
                        ))}
                    </div>
                ) : null}
                <div className="pack-open-modal__header">
                    <img
                        src={packImageSrc}
                        alt={`${seriesName} pack`}
                        className="pack-open-modal__pack-img"
                        onError={handleCardImageError}
                    />
                    <div className="pack-open-modal__header-info">
                        <h2 className="pack-open-modal__title">{seriesName} Pack</h2>
                        <p className="pack-open-modal__count">
                            {visibleCount} of {sortedCards.length} cards revealed
                        </p>
                        {!allRevealed ? (
                            <button
                                className="pack-open-modal__show-all"
                                onClick={showAll}
                            >
                                Show All
                            </button>
                        ) : (
                            <div className="pack-open-modal__footer pack-open-modal__footer--top">
                                {remainingCount > 0 && onOpenAnother ? (
                                    <button
                                        type="button"
                                        className="pack-open-modal__open-another"
                                        onClick={onOpenAnother}
                                        disabled={loading}
                                    >
                                        {loading ? 'Opening...' : `Open Another (${remainingCount} left)`}
                                    </button>
                                ) : null}
                                <button type="button" className="button pack-open-modal__done" onClick={onClose}>
                                    Done
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Screen reader announcer */}
                <div className="sr-only" aria-live="polite" aria-atomic="true">
                    {visibleCount > 0 && visibleCount <= sortedCards.length
                        ? `${safeDecode(sortedCards[visibleCount - 1].name)}, rating ${sortedCards[visibleCount - 1].rating}${sortedCards[visibleCount - 1].CardVariation ? `, ${sortedCards[visibleCount - 1].CardVariation.name} variation` : ''}`
                        : ''}
                </div>

                {/* Card grid — all slots rendered upfront for stable sizing */}
                <div className="pack-open-modal__card-grid" aria-hidden="true">
                    {[...sortedCards].reverse().map((card, gridIndex) => {
                        const sortedIndex = sortedCards.length - 1 - gridIndex;
                        const isRevealed = sortedIndex < visibleCount;
                        const isCeremonyTarget = sortedIndex === ceremonyCardIndex;
                        const showCeremony = isCeremonyActive && isCeremonyTarget;
                        const showCard = isRevealed && !(isCeremonyActive && isCeremonyTarget);
                        const isFlipIn = sortedIndex === flipInCardIndex;
                        const isDimmed = isCeremonyActive && isRevealed && !isCeremonyTarget;
                        const isHidden = !isRevealed && !showCeremony;

                        const wrapperClasses = [
                            'pack-open-modal__card-wrapper',
                            showCard ? `pack-open-modal__card-wrapper--reveal ${getCardRarityClass(card.rating)}` : '',
                            showCeremony ? 'pack-open-modal__card-wrapper--ceremony' : '',
                            isFlipIn ? 'pack-open-modal__card-wrapper--flip-in' : '',
                            isDimmed ? 'pack-open-modal__card-wrapper--dimmed' : '',
                            isHidden ? 'pack-open-modal__card-wrapper--placeholder' : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div
                                key={`${card.cardID}-${card.variationID || 'base'}`}
                                className={wrapperClasses}
                                style={isFlipIn && flipInDuration ? { '--ceremony-flip-duration': flipInDuration } : undefined}
                            >
                                {showCeremony ? (
                                    <CeremonyCard tier={ceremonyTier} phase={ceremonyPhase} />
                                ) : showCard ? (
                                    <Card
                                        card={card}
                                        variation={card.CardVariation || null}
                                        showLabel
                                        fallbackSeriesKey={card.year ? `${card.year}-base` : seriesKey}
                                    />
                                ) : (
                                    <div className="pack-open-modal__card-spacer" />
                                )}
                            </div>
                        );
                    })}
                </div>

            </div>
        </Modal>
    );
};

PackOpenModal.propTypes = {
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            cardID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            year: PropTypes.number.isRequired,
            type: PropTypes.string.isRequired,
            positions: PropTypes.string.isRequired,
            rating: PropTypes.number.isRequired,
        })
    ).isRequired,
    seriesName: PropTypes.string.isRequired,
    seriesKey: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    remainingCount: PropTypes.number,
    onOpenAnother: PropTypes.func,
    loading: PropTypes.bool,
};

export default PackOpenModal;
